import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { canAccessBoringBucket } from '../../lib/entitlements';
import { boringBucketService } from '../../services/boringBucketService';
import '../Dashboard.css';

const TOKEN_STORAGE_KEY = 'bb_signup_token';

/**
 * The Boring Bucket onboarding page — the WFS end of the apply -> approve ->
 * setup-link bridge. Reached from the approval email at
 * /boring-bucket/join?token=<signup token>.
 *
 * - Stashes the signup token so it survives a login round-trip.
 * - If the visitor isn't signed in, routes them to register/login.
 * - If signed in, lets them pick monthly/yearly and starts the BB checkout.
 */
export const BoringBucketJoin: React.FC = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [signupToken, setSignupToken] = useState<string | null>(null);
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancelled = searchParams.get('cancelled') === 'true';
  const isMember = canAccessBoringBucket(user as any);

  // Capture the signup token from the URL and persist it so a login round-trip
  // (which drops query params) doesn't lose it.
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, urlToken);
      setSignupToken(urlToken);
    } else {
      setSignupToken(sessionStorage.getItem(TOKEN_STORAGE_KEY));
    }
  }, [searchParams]);

  const startCheckout = async () => {
    if (!token || !signupToken) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await boringBucketService.startCheckout(token, signupToken, billing);
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      window.location.href = url;
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not start checkout. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="dashboard-container">
      {/* /boring-bucket/join is a plain route — logged-out applicants land
          here too, so the app nav only renders once they're signed in. */}
      {user && <Navigation />}
      <div className="dashboard-content">
        <div style={{ padding: '1.5rem', maxWidth: 620 }}>
          <div className="welcome-section">
            <h2>Set up your Boring Bucket membership</h2>
          </div>

          {isMember ? (
            <Panel>
              <h3 style={h3}>You're already a member.</h3>
              <p style={p}>Your membership is active.</p>
              <Link to="/boring-bucket" style={linkBtn}>Go to The Boring Bucket →</Link>
            </Panel>
          ) : !signupToken ? (
            <Panel>
              <h3 style={h3}>Invitation link required</h3>
              <p style={p}>
                Membership is application-only. Open the setup link from your acceptance
                email, or apply at{' '}
                <a href="https://theboringbucket.com" style={{ color: '#60a5fa' }}>theboringbucket.com</a>.
              </p>
            </Panel>
          ) : !user ? (
            <Panel>
              <h3 style={h3}>First, sign in</h3>
              <p style={p}>
                Create your account — or sign in if you already have one — using the
                <strong style={{ color: '#e2e8f0' }}> same email you applied with</strong>.
                Then return to this page to finish setup.
              </p>
              {/* redirect= sends the user back to the join page after auth;
                  the signup token is held in sessionStorage across the hop. */}
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                <Link to="/register?redirect=boring-bucket/join" style={linkBtn}>Create account</Link>
                <Link to="/login?redirect=boring-bucket/join" style={{ ...linkBtn, background: 'transparent', color: '#60a5fa', border: '1px solid #60a5fa' }}>Sign in</Link>
              </div>
            </Panel>
          ) : (
            <Panel>
              {cancelled && (
                <p style={{ ...p, color: '#fbbf24' }}>Checkout was cancelled — you can start again below.</p>
              )}
              <h3 style={h3}>Choose your billing</h3>
              <div style={{ display: 'flex', gap: 10, margin: '12px 0' }}>
                <BillingOption
                  selected={billing === 'monthly'}
                  onClick={() => setBilling('monthly')}
                  label="Monthly" price="$299 / mo"
                />
                <BillingOption
                  selected={billing === 'yearly'}
                  onClick={() => setBilling('yearly')}
                  label="Yearly" price="$2,990 / yr" note="2 months free"
                />
              </div>
              <p style={{ ...p, fontSize: 12, color: '#64748b' }}>
                Membership includes the full platform, collective mentoring, the community,
                and the live Founder's Trades view. Cancel anytime from the billing portal.
              </p>
              {error && <p style={{ ...p, color: '#f87171' }}>{error}</p>}
              <button
                onClick={startCheckout}
                disabled={busy}
                style={{
                  marginTop: 8, width: '100%', padding: '12px 0',
                  background: busy ? '#475569' : '#10b981', color: '#0a0f1e',
                  fontWeight: 700, fontSize: 15, border: 'none', borderRadius: 8,
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >
                {busy ? 'Starting checkout…' : 'Start membership →'}
              </button>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
};

const BillingOption: React.FC<{
  selected: boolean; onClick: () => void; label: string; price: string; note?: string;
}> = ({ selected, onClick, label, price, note }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1, textAlign: 'left', cursor: 'pointer',
      background: selected ? 'rgba(16,185,129,0.12)' : 'rgba(255,255,255,0.04)',
      border: `1px solid ${selected ? '#10b981' : 'rgba(255,255,255,0.12)'}`,
      borderRadius: 8, padding: '12px 14px',
    }}
  >
    <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 18, color: '#f9fafb', fontWeight: 700, marginTop: 2 }}>{price}</div>
    {note && <div style={{ fontSize: 11, color: '#10b981', marginTop: 2 }}>{note}</div>}
  </button>
);

const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, padding: '22px 24px',
  }}>{children}</div>
);

const h3: React.CSSProperties = { margin: '0 0 8px', color: '#f9fafb', fontSize: '1.1em' };
const p: React.CSSProperties = { color: '#94a3b8', fontSize: 14, lineHeight: 1.7, margin: '0 0 8px' };
const linkBtn: React.CSSProperties = {
  display: 'inline-block', background: '#10b981', color: '#0a0f1e', fontWeight: 700,
  fontSize: 14, padding: '10px 22px', borderRadius: 8, textDecoration: 'none',
};

export default BoringBucketJoin;
